<div class="dialog-title">
  @if (!isCreate) {
    <div class="link-holder" mat-dialog-title>
      <a [href]="copyText" target="_blank">
        {{ dialogTitle }}
      </a>
      <button class="link-button" (click)="copyLink()"><mat-icon>link</mat-icon></button>
    </div>
  } @else {
    <h2 class="link-holder" mat-dialog-title>
      {{ dialogTitle }}
    </h2>
  }
  <div class="header-actions">
    @if (!isReadonly && !isCreate) {
      <button mat-icon-button color="warn" (click)="onDelete()" matTooltip="Delete task">
        <mat-icon>delete</mat-icon>
      </button>
    }
    @if (loading$ | async) {
      <mat-progress-spinner [diameter]="40" mode="indeterminate"></mat-progress-spinner>
    }
  </div>
</div>

<mat-dialog-content class="dialog-content">
  <form [formGroup]="taskForm" class="dialog-grid">
    <div class="left-column">
      <mat-form-field [class.readonly]="isReadonly" appearance="outline" class="full-width">
        <mat-label>Title</mat-label>
        <input matInput [readonly]="isReadonly" formControlName="title" placeholder="Brief summary of the task" />
        @if (taskFormControls.title.hasError('required')) {
          <mat-error>Title is required.</mat-error>
        }
      </mat-form-field>

      <div class="description-content">
        @if (!isReadonly) {
          <button (click)="useMdDescription = !useMdDescription" class="description-button">
            {{ useMdDescription ? 'Edit' : 'Preview' }}
          </button>
        }
        @if (useMdDescription) {
          <div class="md-content-holder">
            <markdown [data]="taskFormControls.description.value"></markdown>
            <p>Description</p>
          </div>
        } @else {
          <mat-form-field [class.readonly]="isReadonly" appearance="outline" class="full-width description-field">
            <mat-label>Description</mat-label>
            <textarea
              [readonly]="isReadonly"
              matInput
              formControlName="description"
              rows="15"
              placeholder="Detailed description..."></textarea>
          </mat-form-field>
        }
      </div>
    </div>

    <div class="right-column">
      <mat-form-field [class.readonly]="isReadonly" appearance="outline" class="full-width task-status-field">
        <mat-select formControlName="status">
          @for (status of statusOptions; track status) {
            <mat-option [value]="status">{{ status | kanbanId }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (reporter$ | async; as reporter) {
        <div class="reporter-display-card">
          <span class="label"
            >Reporter:
            <span
              class="reporter-type-chip"
              [class.chip-user]="reporter.type === 'user'"
              [class.chip-persona]="reporter.type === 'persona'">
              {{ reporter.type | titlecase }}
            </span></span
          >

          <div class="reporter-info">
            <span class="reporter-name">{{ reporter.name }}</span>
          </div>
        </div>
      }
      <mat-form-field [class.readonly]="isReadonly" appearance="outline">
        <mat-label>Responsible</mat-label>
        <mat-select formControlName="responsibleId">
          @for (assignee of assignees$ | async; track assignee.id) {
            <mat-option [value]="assignee.id">
              <mat-icon
                [color]="assignee.type === 'user' ? 'primary' : 'accent'"
                [matTooltip]="assignee.type | titlecase">
                {{ assignee.type === 'user' ? 'person' : 'smart_toy' }}
              </mat-icon>
              {{ assignee | shortId }}
            </mat-option>
          }
        </mat-select>
        @if (taskFormControls.responsibleId.hasError('required')) {
          <mat-error>Responsible assignment is required.</mat-error>
        }
      </mat-form-field>

      <mat-form-field [class.readonly]="isReadonly" appearance="outline" class="priority-control">
        <mat-label>Priority</mat-label>
        <mat-select formControlName="priority">
          <mat-select-trigger>
            @if (taskFormControls.priority.value; as priorityValue) {
              <div class="priority-options">
                <div [class]="'priority-' + priorityValue + '-bg'" class="round"></div>
                <span>{{ priorityValue }}</span>
              </div>
            }
          </mat-select-trigger>
          <mat-option [value]="1">
            <div class="priority-options">
              <div class="round priority-1"></div>
              1
            </div>
          </mat-option>
          <mat-option [value]="2">
            <div class="priority-options">
              <div class="round priority-2"></div>
              2
            </div>
          </mat-option>
          <mat-option [value]="3">
            <div class="priority-options">
              <div class="round priority-3"></div>
              3
            </div>
          </mat-option>
          <mat-option [value]="4">
            <div class="priority-options">
              <div class="round priority-4"></div>
              4
            </div>
          </mat-option>
          <mat-option [value]="5">
            <div class="priority-options">
              <div class="round priority-5"></div>
              5
            </div>
          </mat-option>
        </mat-select>
        @if (taskFormControls.priority.hasError('required')) {
          <mat-error>Priority is required.</mat-error>
        }
      </mat-form-field>

      <mat-form-field [class.readonly]="isReadonly" appearance="outline" class="full-width">
        <mat-label>Deadline</mat-label>
        <input [readonly]="isReadonly" matInput [matDatepicker]="picker" formControlName="deadline" />
        <mat-hint>MM/DD/YYYY</mat-hint>
        <mat-datepicker-toggle [disabled]="isReadonly" matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-datepicker [disabled]="isReadonly" #picker></mat-datepicker>
      </mat-form-field>

      <app-tag-input
        [readonly]="isReadonly"
        [class.readonly]="isReadonly"
        formControlName="tags"></app-tag-input>
    </div>
  </form>
  @if (!isReadonly) {
    <app-loader-button
      [disabled]="taskForm.invalid || !!(isDisabledEdit$ | async)"
      mat-flat-button
      color="primary"
      (click)="onSave()"
      [loading]="isSaving">
      Save
    </app-loader-button>
  }

  @if (!isCreate) {
    <mat-tab-group animationDuration="0ms" class="history-comment-tabs">
      <mat-tab [label]="'History'">
        <ng-template matTabContent>
          <app-task-history [workspaceId]="data.workspaceId" [taskId]="data.taskId!"></app-task-history>
        </ng-template>
      </mat-tab>
      <mat-tab [label]="'Comments'">
        <ng-template matTabContent>
          <app-task-comments [workspaceId]="data.workspaceId" [taskId]="data.taskId!"></app-task-comments>
        </ng-template>
      </mat-tab>
    </mat-tab-group>
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="onCancel()">Close</button>
</mat-dialog-actions>
